// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type PartCatalog, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import type { SelectProps } from '@mantine/core';

import {
  SearchableSelect,
  type SearchableSelectProps,
} from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { isNil } from 'helpers/isNotNil';

type Props = {
  selectedCatalog: PartCatalog | null;
  onCatalogSelected: (partCatalog: PartCatalog | null) => void;
  required?: boolean;
  disabled?: boolean;
  showLabel?: boolean;
  selectProps?: Partial<SearchableSelectProps<'shop/partCatalog', PartCatalog, 'partCatalogId'>>;
  onlyEditable?: boolean;
  autoselect?: boolean;
  /** Width */
  w?: SelectProps['w'];
};

export const CatalogSelect = ({
  selectedCatalog,
  disabled,
  required,
  showLabel,
  selectProps,
  onCatalogSelected,
  autoselect = true,
  onlyEditable,
  w,
}: Props) => {
  const [catalogSearchPhrase, setCatalogSearchPhrase] = useState('');
  const partCatalogPaginatedGet = useEvolvePaginatedGet('shop/partCatalog', {
    defaultConfig: {
      params: {
        editable: onlyEditable ? true : undefined,
      },
    },
  });

  return (
    <SearchableSelect
      label={showLabel ? 'Catalog' : undefined}
      placeholder="Select a catalog..."
      {...(isNil(selectedCatalog) && autoselect
        ? {
            autoFocus: true,
            ['data-autofocus']: true,
            autoSelectWhenOnlyOneOption: true,
          }
        : {})}
      blurOnChange
      idKey="partCatalogId"
      paginatedGet={partCatalogPaginatedGet}
      value={selectedCatalog}
      onChange={onCatalogSelected}
      getItemLabel={(c) => c.name}
      getGroup={(c) => (c.editable ? 'Company catalogs' : 'EVOLVE catalogs')}
      w={w}
      searchKey="name"
      searchValue={catalogSearchPhrase}
      onSearchChange={setCatalogSearchPhrase}
      disabled={disabled}
      required={required}
      {...selectProps}
    />
  );
};
